// 03 §18 machines — finance and core. Aligned names: gl.open/gl.close/gl.reopen → periods.open/
// periods.close/periods.reopen (02 §8.3 `periods`), ar.post → ar.post_invoice, sage_bridge.export
// stays (standard verb), `(override)` on bank reopen → bank.close (CR-BG2c: override code BANK_REC_REOPEN, [assumption Q-AR-07]; was
// gl.override).
import { defineMachine, systemStep as D, userStep as U } from './machine.ts';
import type { Transition } from './machine.ts';

export const FISCAL_PERIOD = defineMachine(
  'finance.fiscal_period.status',
  ['future', 'open', 'closing', 'closed', 'reopened'],
  ['future'],
  [
    U('future', 'open', 'open', 'periods.open'),
    U('open', 'closing', 'start_close', 'periods.close'),
    U(['closing', 'reopened'], 'closed', 'close', 'periods.close'),
    U('closed', 'reopened', 'reopen', 'periods.reopen', {
      reason: true,
      stepUp: true,
      overrideOnly: true,
    }),
  ],
);

export const JOURNAL_ENTRY = defineMachine(
  'finance.journal_entry.status',
  ['draft', 'awaiting_approval', 'posted', 'reversed', 'cancelled'],
  ['draft'],
  [
    U('draft', 'awaiting_approval', 'submit', 'gl.create'),
    U('awaiting_approval', 'posted', 'approve', 'gl.approve', {
      sod: true,
      approvalLimit: true,
      gates: ['G7'],
    }),
    U('posted', 'reversed', 'reverse', 'gl.reverse', { gates: ['G7'] }),
    U('draft', 'cancelled', 'cancel', 'gl.edit'),
  ],
);

const AP_PRE_PAID = ['draft', 'awaiting_approval', 'approved', 'posted'] as const;
type ApState = (typeof AP_PRE_PAID)[number] | 'on_hold' | 'partially_paid' | 'paid' | 'cancelled';
export const AP_DOCUMENT = defineMachine(
  'finance.ap_document.status',
  [
    'draft',
    'awaiting_approval',
    'approved',
    'on_hold',
    'posted',
    'partially_paid',
    'paid',
    'cancelled',
  ],
  ['draft'],
  [
    U('draft', 'awaiting_approval', 'submit', 'ap.create'),
    U('awaiting_approval', 'approved', 'approve', 'ap.approve', { sod: true, approvalLimit: true }),
    U('approved', 'posted', 'post', 'ap.post', { gates: ['G7'] }),
    U(AP_PRE_PAID, 'on_hold', 'hold', 'ap.hold', { reason: true }),
    // release returns to the status held before (`to` chosen from the stored hold_from_status)
    ...AP_PRE_PAID.map((to): Transition<ApState> => U('on_hold', to, 'release_hold', 'ap.hold')),
    D('posted', 'partially_paid', 'partial_payment'),
    D(['posted', 'partially_paid'], 'paid', 'fully_paid'),
    // CR-BG2c (W2c.ap R-ENUM-AP-1): a voided payment re-derives the status from what is still paid (BR-AP-034).
    D('paid', 'partially_paid', 'payment_voided'),
    D(['paid', 'partially_paid'], 'posted', 'payment_voided'),
    U('draft', 'cancelled', 'cancel', 'ap.edit'),
  ],
);

export const AR_DOCUMENT = defineMachine(
  'finance.ar_document.status',
  ['draft', 'issued', 'partially_paid', 'paid', 'credited', 'cancelled'],
  ['draft'],
  [
    U('draft', 'issued', 'issue', 'ar.post_invoice', { gates: ['G7'] }),
    // CR-BG2c (W2c.ar-bank R-CORE-AR-5): a credit note is issued under ar.credit (BR-AR-011), never ar.post_invoice.
    U('draft', 'issued', 'issue_credit_note', 'ar.credit', { gates: ['G7'] }),
    D('issued', 'partially_paid', 'partial_receipt'),
    D(['issued', 'partially_paid'], 'paid', 'fully_paid'),
    D(['issued', 'partially_paid'], 'credited', 'credit_note_applied'),
    // CR-BG2c (R-CORE-AR-5): statuses are derived both ways; a voided receipt or credit reopens the invoice.
    D('paid', 'partially_paid', 'receipt_voided'),
    D(['paid', 'partially_paid'], 'issued', 'receipt_voided'),
    U('draft', 'cancelled', 'cancel', 'ar.edit'),
  ],
);

export const CUSTOMER_DEDUCTION = defineMachine(
  'finance.customer_deduction.status',
  ['open', 'validated', 'disputed', 'accepted', 'recovered', 'written_off'],
  ['open'],
  [
    U('open', 'validated', 'validate', 'ar.edit'),
    U('open', 'disputed', 'dispute', 'ar.edit', { reason: true }),
    D('validated', 'accepted', 'credit_note_issued'),
    U('disputed', 'recovered', 'recover', 'ar.write_off'),
    U('disputed', 'written_off', 'write_off', 'ar.write_off', { reason: true }),
  ],
);

/**
 * AP payments use ap.*; AR receipts post with ar.apply_receipt [assumption: 03 "ap.post/ar.post"]. CR-BG2c (W2c.ar-bank
 * R-CORE-AR-5, W2c.ap R-ENUM-AP-3): AR receipts are recorded and posted in one step (`post_receipt`) and voided under
 * ar.override (`void_receipt`); a payment run settles its payments as a system step (`settled_by_run`). The actions
 * are distinct per direction, so no AR key opens an AP step and no AP key an AR step; the caller picks the action from
 * `payment.direction`.
 */
export const PAYMENT = defineMachine(
  'finance.payment.status',
  ['draft', 'approved', 'posted', 'reconciled', 'void'],
  ['draft'],
  [
    U('draft', 'approved', 'approve', 'ap.approve', { sod: true, approvalLimit: true }),
    U('approved', 'posted', 'post', 'ap.post', { gates: ['G7'] }),
    U('draft', 'posted', 'post_receipt', 'ar.apply_receipt', { gates: ['G7'] }),
    D('draft', 'posted', 'settled_by_run'),
    D('posted', 'reconciled', 'bank_matched'),
    U('posted', 'void', 'void', 'ap.void', { reason: true }),
    U('posted', 'void', 'void_receipt', 'ar.override', { reason: true }),
  ],
);

export const PAYMENT_RUN = defineMachine(
  'finance.payment_run.status',
  ['draft', 'proposed', 'approved', 'exported', 'posted', 'cancelled'],
  ['draft'],
  [
    U('draft', 'proposed', 'propose', 'ap.create'),
    U('proposed', 'approved', 'approve', 'ap.approve', { sod: true, approvalLimit: true }),
    U('approved', 'exported', 'export', 'ap.release_payment', { stepUp: true, sod: true }),
    U('exported', 'posted', 'post', 'ap.post', { gates: ['G7'] }),
    // CR-BG2c (W2c.ap R-ENUM-AP-2): parallel mode, the run is paid in Sage; a second person confirms (BR-AP-036).
    U('approved', 'posted', 'confirm_in_sage', 'ap.post', { sod: true, gates: ['G7'] }),
    U(['draft', 'proposed', 'approved'], 'cancelled', 'cancel', 'ap.edit'),
  ],
);

export const BANK_RECONCILIATION = defineMachine(
  'finance.bank_reconciliation.status',
  ['in_progress', 'reconciled', 'reopened'],
  ['in_progress'],
  [
    U(['in_progress', 'reopened'], 'reconciled', 'close', 'bank.close'),
    U('reconciled', 'reopened', 'reopen', 'bank.close', { overrideOnly: true, reason: true }),
  ],
);

export const FX_REVALUATION = defineMachine(
  'finance.fx_revaluation.status',
  ['draft', 'posted', 'reversed'],
  ['draft'],
  [
    U('draft', 'posted', 'post', 'gl.approve', { gates: ['G7'] }),
    D('posted', 'reversed', 'auto_reverse_next_period'),
  ],
);

export const SAGE_EXPORT_BATCH = defineMachine(
  'finance.sage_export_batch.status',
  ['draft', 'generated', 'downloaded', 'imported_confirmed', 'failed', 'void'],
  ['draft'],
  [
    D('draft', 'generated', 'generated'),
    U('generated', 'downloaded', 'download', 'sage_bridge.export'),
    U('downloaded', 'imported_confirmed', 'mark_imported', 'sage_bridge.mark_imported'),
    U(['generated', 'downloaded'], 'failed', 'mark_failed', 'sage_bridge.generate', {
      reason: true,
    }),
    U(['generated', 'downloaded'], 'void', 'void', 'sage_bridge.generate', { reason: true }),
  ],
);

/** The subject document's approve permission applies (within limit, ≠ requester). */
export const APPROVAL_REQUEST = defineMachine(
  'core.approval_request.status',
  ['pending', 'approved', 'rejected', 'withdrawn', 'applied'],
  ['pending'],
  [
    U('pending', 'approved', 'approve', null, {
      permissionFromSubject: true,
      sod: true,
      approvalLimit: true,
    }),
    U('pending', 'rejected', 'reject', null, {
      permissionFromSubject: true,
      sod: true,
      reason: true,
    }),
    U('pending', 'withdrawn', 'withdraw', null, { permissionFromSubject: true }),
    // CR-W2b-12 (CR-W2b-3 R-APR-1): the approved change was applied once (bound by `payload_hash`); never again.
    D('approved', 'applied', 'applied'),
  ],
);

/** Agent retries a failed job up to 5 times; ops.admin cancels queued jobs. */
export const PRINT_JOB = defineMachine(
  'core.print_job.status',
  ['queued', 'sent', 'printed', 'failed', 'cancelled'],
  ['queued'],
  [
    D('queued', 'sent', 'sent_to_agent'),
    D('sent', 'printed', 'agent_printed'),
    D(['queued', 'sent'], 'failed', 'agent_failed'),
    D('failed', 'queued', 'retry'),
    U('queued', 'cancelled', 'cancel', 'ops.admin'),
  ],
);

export const JOB_RUN = defineMachine(
  'core.job_run.status',
  ['queued', 'running', 'succeeded', 'failed', 'cancelled'],
  ['queued'],
  [
    D('queued', 'running', 'started'),
    D('running', 'succeeded', 'succeeded'),
    D('running', 'failed', 'failed'),
    D('failed', 'queued', 'retry'),
    // CR-W1c-11(d): Ops can cancel a failed job as well as a queued one (BR-JOB-001 "Retry / Cancel").
    U(['queued', 'failed'], 'cancelled', 'cancel', 'ops.admin'),
  ],
);

/** Retry ceiling for print jobs and job runs [03 §18 "retry ≤ 5"]. */
export const JOB_MAX_ATTEMPTS = 5;
